const features = [
  {
    icon: "ri-shield-check-line",
    color: "sky",
    title: "Advanced Encryption",
    desc: "All data transmitted through our VPN is protected with AES-256 encryption — the same standard used by governments and financial institutions worldwide.",
  },
  {
    icon: "ri-speed-up-line",
    color: "emerald",
    title: "Blazing Fast Performance",
    desc: "Our apps are engineered for speed. Minimal resource usage means your device stays responsive, your battery lasts longer, and everything runs smoother.",
  },
  {
    icon: "ri-eye-off-line",
    color: "violet",
    title: "Complete Privacy",
    desc: "We operate a strict zero-log policy. Your browsing history, connection timestamps, and personal data are never stored, shared, or sold.",
  },
  {
    icon: "ri-hard-drive-3-line",
    color: "amber",
    title: "Smart Storage Management",
    desc: "Cleaner intelligently identifies redundant files, duplicate media, and bloated caches — reclaiming gigabytes of storage with a single tap.",
  },
  {
    icon: "ri-smartphone-line",
    color: "sky",
    title: "Native iOS Experience",
    desc: "Built exclusively for iOS using native frameworks. Every interaction feels natural, fluid, and perfectly aligned with Apple's Human Interface Guidelines.",
  },
  {
    icon: "ri-refresh-line",
    color: "emerald",
    title: "Automatic Updates",
    desc: "Our apps stay current with the latest iOS releases and security patches, ensuring you always have the most protected and optimized version available.",
  },
];

const colorMap: Record<string, { bg: string; icon: string; border: string }> = {
  sky: { bg: "bg-sky-500/10", icon: "text-sky-400", border: "border-sky-500/20" },
  emerald: { bg: "bg-emerald-500/10", icon: "text-emerald-400", border: "border-emerald-500/20" },
  violet: { bg: "bg-violet-500/10", icon: "text-violet-400", border: "border-violet-500/20" },
  amber: { bg: "bg-amber-500/10", icon: "text-amber-400", border: "border-amber-500/20" },
};

export default function FeaturesSection() {
  return (
    <section id="features" className="w-full py-24 md:py-32 bg-gray-950">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="flex items-center gap-2 mb-4">
          <span className="w-8 h-px bg-gray-600" />
          <span className="text-xs font-semibold tracking-widest uppercase text-gray-500">Features</span>
        </div>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight max-w-lg">
            Everything You Need.<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-400 to-teal-400">
              Nothing You Don&apos;t.
            </span>
          </h2>
          <p className="text-gray-400 text-base max-w-sm leading-relaxed">
            Thoughtfully designed features that work together to deliver a seamless, secure mobile experience.
          </p>
        </div>

        {/* Feature Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((f) => {
            const c = colorMap[f.color] || colorMap.sky;
            return (
              <div
                key={f.title}
                className={`rounded-2xl border ${c.border} bg-gray-900 p-7 flex flex-col gap-4 hover:bg-gray-800 transition-colors duration-200`}
              >
                <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${c.bg}`}>
                  <i className={`${f.icon} ${c.icon} text-xl`} />
                </div>
                <div>
                  <h4 className="text-white font-semibold text-base mb-2">{f.title}</h4>
                  <p className="text-gray-400 text-sm leading-relaxed">{f.desc}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA strip */}
        <div className="mt-16 rounded-3xl bg-gradient-to-r from-sky-900/50 to-teal-900/50 border border-sky-800/30 p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-white text-2xl font-bold mb-2">Ready to secure your iOS device?</h3>
            <p className="text-gray-400 text-sm">Get in touch with our team to learn more about our products.</p>
          </div>
          <a
            href="#contact"
            onClick={(e) => { e.preventDefault(); document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" }); }}
            className="bg-white text-gray-900 font-semibold text-sm px-8 py-3.5 rounded-full cursor-pointer hover:bg-gray-100 transition-colors whitespace-nowrap shrink-0"
          >
            Contact Us
          </a>
        </div>
      </div>
    </section>
  );
}
